/**
 * VoiceInputButton
 *
 * Reusable microphone button that records audio via MediaRecorder,
 * sends it to POST /api/voice/transcribe, and delivers the transcript
 * back to the parent via onTranscript(text).
 *
 * Also renders a small language dropdown (Auto + common languages).
 */

import React, { useEffect, useRef, useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_LANGUAGES = [
  { code: 'auto', label: 'Auto-detect' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'nl', label: 'Dutch' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ar', label: 'Arabic' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ru', label: 'Russian' },
  { code: 'pl', label: 'Polish' },
  { code: 'tr', label: 'Turkish' },
  { code: 'vi', label: 'Vietnamese' },
];

// Prefer webm/opus (Chrome), fall back to mp4 (Safari)
function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/mpeg'];
  for (const t of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(t)) return t;
    } catch (_e) { /* noop */ }
  }
  return null;
}

const VoiceInputButton = ({
  onTranscript,
  contextHint = 'meal log with common food names and portion sizes',
  disabled = false,
}) => {
  const [status, setStatus] = useState('idle'); // idle | recording | uploading | error
  const [error, setError] = useState('');
  const [language, setLanguage] = useState('auto');
  const [elapsed, setElapsed] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startTsRef = useRef(0);
  const tickRef = useRef(null);

  const supportsMediaRecorder = typeof window !== 'undefined'
    && !!(window.MediaRecorder && navigator?.mediaDevices?.getUserMedia);

  useEffect(() => () => stopStream(), []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch (_e) { /* noop */ } });
      streamRef.current = null;
    }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  const startRecording = async () => {
    setError('');
    if (!supportsMediaRecorder) {
      setError('Your browser does not support voice recording.');
      setStatus('error');
      return;
    }
    try {
      const mimeType = pickMimeType();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => handleUpload(mimeType);

      mediaRecorderRef.current = recorder;
      recorder.start();
      startTsRef.current = Date.now();
      setElapsed(0);
      tickRef.current = setInterval(() => {
        setElapsed(Math.round((Date.now() - startTsRef.current) / 1000));
      }, 500);
      setStatus('recording');
    } catch (e) {
      const msg = (e && e.name === 'NotAllowedError')
        ? 'Microphone access denied. Enable it in your browser settings.'
        : (e?.message || 'Could not access the microphone.');
      setError(msg);
      setStatus('error');
      stopStream();
    }
  };

  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    } catch (_e) { /* noop */ }
  };

  const handleUpload = async (mimeType) => {
    stopStream();
    setStatus('uploading');

    const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
    if (!blob || blob.size === 0) {
      setError('No audio captured. Try again.');
      setStatus('error');
      return;
    }

    const ext = (mimeType && mimeType.includes('mp4')) ? 'mp4'
      : (mimeType && mimeType.includes('mpeg')) ? 'mp3'
      : 'webm';

    const form = new FormData();
    form.append('audio', blob, `recording.${ext}`);
    form.append('language', language);
    if (contextHint) form.append('context', contextHint);

    try {
      const res = await fetch(`${BACKEND_URL}/api/voice/transcribe`, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        const detail = errBody?.detail || `Transcription failed (HTTP ${res.status}).`;
        setError(typeof detail === 'string' ? detail : 'Transcription failed.');
        setStatus('error');
        return;
      }
      const data = await res.json();
      const text = (data?.transcript || '').trim();
      if (!text) {
        setError('Nothing was transcribed. Try speaking closer to the mic.');
        setStatus('error');
        return;
      }
      onTranscript(text, data);
      setStatus('idle');
      setElapsed(0);
    } catch (e) {
      setError(e?.message || 'Network error while uploading audio.');
      setStatus('error');
    }
  };

  const isRecording = status === 'recording';
  const isUploading = status === 'uploading';

  return (
    <div
      data-testid="voice-input-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '12px',
        marginBottom: '12px',
        borderRadius: '10px',
        background: 'rgba(124, 92, 252, 0.06)',
        border: '1px solid rgba(124, 92, 252, 0.22)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button
          type="button"
          onClick={isRecording ? stopRecording : startRecording}
          disabled={disabled || isUploading}
          data-testid={isRecording ? 'voice-stop-btn' : 'voice-record-btn'}
          aria-label={isRecording ? 'Stop recording' : 'Start voice input'}
          style={{
            flex: '0 0 auto',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: 'none',
            cursor: (disabled || isUploading) ? 'not-allowed' : 'pointer',
            background: isRecording
              ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
              : 'linear-gradient(135deg, #7c5cfc 0%, #5b3fd6 100%)',
            color: 'white',
            fontSize: '20px',
            boxShadow: isRecording
              ? '0 0 0 6px rgba(239, 68, 68, 0.2)'
              : '0 4px 12px rgba(124, 92, 252, 0.35)',
            animation: isRecording ? 'voicePulse 1.2s ease-in-out infinite' : 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isUploading ? '⏳' : (isRecording ? '■' : '🎙️')}
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
            {isRecording ? `Recording… ${elapsed}s` : isUploading ? 'Transcribing…' : 'Voice input'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            {isRecording
              ? 'Speak clearly — tap ■ when finished'
              : 'Tap the mic and describe your meal'}
          </div>
        </div>

        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          disabled={isRecording || isUploading}
          data-testid="voice-language-select"
          aria-label="Voice language"
          style={{
            flex: '0 0 auto',
            padding: '6px 8px',
            fontSize: '12px',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
            background: 'var(--input-bg)',
            color: 'var(--text-primary)',
            cursor: (isRecording || isUploading) ? 'not-allowed' : 'pointer',
          }}
        >
          {DEFAULT_LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div
          data-testid="voice-error"
          style={{
            fontSize: '12px',
            color: '#ef4444',
            padding: '6px 8px',
            borderRadius: '6px',
            background: 'rgba(239, 68, 68, 0.08)',
          }}
        >
          {error}
        </div>
      )}

      <style>{`
        @keyframes voicePulse {
          0%   { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); }
          70%  { box-shadow: 0 0 0 14px rgba(239, 68, 68, 0); }
          100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
      `}</style>
    </div>
  );
};

export default VoiceInputButton;
